import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { debounceTime, merge, startWith } from 'rxjs';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { getControl, getFormArray, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { Utilities } from '@core/helpers/utilities';
import { cloneDeep } from 'lodash-es';
import { formPaths } from '@shared/constants/form-paths.const';
import { getDatiIdentificativiControl } from '../validators/helpers/dati-identificativi-form-validators.helper';
import { withInitialValueDebounced } from '@shared/operators/with-initial-value-debounced.operator';

export const PERIODIC_REVIEW_COEC_A12_THRESHOLD = 22_523_684.69;
export const setDatiIdentificativiCalcoliListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_GiorniDurataRevisione(form, store);
    setCkPeriodicReviFromCkEnteTenutoCertBil(form, store);
    setCkPeriodicReviFromElSocietaPartecipate(form, store);
    setCkPeriodicReviFromCkIscrizAlbo(form, store);
    setCkPeriodicReviFromCoEcA12(form, store);
    setTipoIncaricoEffettivoListeners(form, store);
};

const my_GiorniDurataRevisione = (form: FormGroup, store: Store<Revisione53State>): void => {
    const dataInizioControl = getDatiIdentificativiControl(form, 'my_DataRevisioneDal');
    const dataFineControl = getControl(form, `${formPaths.conclusioni}.my_DataCompilaz`);

    merge(dataInizioControl.valueChanges.pipe(startWith(dataInizioControl.value)), dataFineControl.valueChanges.pipe(startWith(dataFineControl.value)))
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            calcolaGiorniDurataRevisione(form, store);
        });
};

const calcolaGiorniDurataRevisione = (form: FormGroup, store: Store<Revisione53State>): void => {
    const dataInizioRaw = getDatiIdentificativiControl(form, 'my_DataRevisioneDal')?.value;
    const dataFineRaw = getControl(form, `${formPaths.conclusioni}.my_DataCompilaz`)?.value;
    const giorniDurataRevisioneControl = getDatiIdentificativiControl(form, 'my_GiorniDurataRevisione');

    let giorniDurata = '';

    if (!isNilOrEmpty(dataInizioRaw) && !isNilOrEmpty(dataFineRaw)) {
        const dataInizio = parseISO(dataInizioRaw);
        const dataFine = parseISO(dataFineRaw);

        if (!isNaN(dataInizio.getTime()) && !isNaN(dataFine.getTime())) {
            giorniDurata = differenceInCalendarDays(dataFine, dataInizio).toString(10);
        }
    }

    giorniDurataRevisioneControl.patchValue(giorniDurata, { emitEvent: false });

    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
        myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_GiorniDurataRevisione = giorniDurata;
        return myVerbaleClone;
    });
};

/** Sets my_CkPeriodicRevi to '1' when the cooperative is subject to mandatory certification review. */
const setCkPeriodicReviFromCkEnteTenutoCertBil = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getControl(form, `${formPaths.bilancio}.my_CkEnteTenutoCertBil`);
    control.valueChanges.pipe(withInitialValueDebounced(control)).subscribe((value) => {
        if (value === '1') {
            setCkPeriodicRevi(form, store);
        }
    });
};

/** Sets my_CkPeriodicRevi to '1' when a participated SPA company holds more than 50% stake. */
export const setCkPeriodicReviFromElSocietaPartecipate = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getFormArray(form, `${formPaths.bilancio}.my_SocietaPartecipate.my_ElSocietaPartecipate`);
    control.valueChanges.pipe(withInitialValueDebounced(control)).subscribe((elements: any[]) => {
        const shouldSet = (elements ?? []).some((el) => el.my_TipoSocietaPartec === '2' && el.my_PercSpaSrl > 50);
        if (shouldSet) {
            setCkPeriodicRevi(form, store);
        }
    });
};

/** Sets my_CkPeriodicRevi to '1' when the cooperative is not registered in the Albo (ckIscrizAlbo = '2'). */
export const setCkPeriodicReviFromCkIscrizAlbo = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getControl(form, `${formPaths.schedaCoopEdil}.my_CkIscrizAlbo`);
    control.valueChanges.pipe(withInitialValueDebounced(control)).subscribe((value) => {
        if (value === '2') {
            setCkPeriodicRevi(form, store);
        }
    });
};

const setCkPeriodicReviFromCoEcA12 = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getControl(form, `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcA12`);
    control.valueChanges.pipe(withInitialValueDebounced(control)).subscribe((value) => {
        if (toNum(value) >= PERIODIC_REVIEW_COEC_A12_THRESHOLD) {
            setCkPeriodicRevi(form, store);
        }
    });
};

const setCkPeriodicRevi = (form: FormGroup, store: Store<Revisione53State>): void => {
    const control = getDatiIdentificativiControl(form, 'my_CkPeriodicRevi');
    control.patchValue('1', { emitEvent: false });
    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
        myVerbaleClone.my_SezioneI__Rilevazione.my_DatiIdentificativi.my_CkPeriodicRevi = '1';
        return myVerbaleClone;
    });
};

const setTipoIncaricoEffettivoListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    const conclusioniGroup = getGroup(form, formPaths.conclusioni);

    const tipoIncaricoControl = getDatiIdentificativiControl(form, 'my_TipoIncarico');
    const ckPeriodicReviControl = getDatiIdentificativiControl(form, 'my_CkPeriodicRevi');
    const anno1Control = getDatiIdentificativiControl(form, 'my_Anno1');
    const anno2Control = getDatiIdentificativiControl(form, 'my_Anno2');
    const dataCompilazControl = getControl(conclusioniGroup, 'my_DataCompilaz');

    merge(
        tipoIncaricoControl.valueChanges.pipe(startWith(tipoIncaricoControl.value)),
        ckPeriodicReviControl.valueChanges.pipe(startWith(ckPeriodicReviControl.value)),
        anno1Control.valueChanges.pipe(startWith(anno1Control.value)),
        anno2Control.valueChanges.pipe(startWith(anno2Control.value)),
        dataCompilazControl.valueChanges.pipe(startWith(dataCompilazControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            aggiornaTipoIncaricoEffettivo(form, store);
        });
};

const aggiornaTipoIncaricoEffettivo = (form: FormGroup, store: Store<Revisione53State>): void => {
    const conclusioniGroup = getGroup(form, formPaths.conclusioni);

    const tipoIncarico = getDatiIdentificativiControl(form, 'my_TipoIncarico')?.value as string;
    const ckPeriodicRevi = getDatiIdentificativiControl(form, 'my_CkPeriodicRevi')?.value as string;
    const anno1 = getDatiIdentificativiControl(form, 'my_Anno1')?.value as string;
    const anno2 = getDatiIdentificativiControl(form, 'my_Anno2')?.value as string;
    const dataCompilaz = getControl(conclusioniGroup, 'my_DataCompilaz')?.value as string;

    const tipoIncaricoEffettivo = calcolaTipoIncaricoEffettivo(tipoIncarico, ckPeriodicRevi, anno2, dataCompilaz);

    const { tipoRevisione, annoRevisione } = calcolaTipoRevisioneAnnuale(tipoIncaricoEffettivo, anno1, anno2);

    getDatiIdentificativiControl(form, 'my_TipoIncaricoEffettivo')?.patchValue(tipoIncaricoEffettivo, {
        emitEvent: false,
    });

    getDatiIdentificativiControl(form, 'my_TipoRevisione')?.patchValue(tipoRevisione, {
        emitEvent: false,
    });

    getDatiIdentificativiControl(form, 'my_AnnoRevisionePerAnnuali')?.patchValue(annoRevisione, {
        emitEvent: false,
    });

    updateVerbaleOnStore(store, (myVerbale) => {
        const clone: MyVerbale = cloneDeep(myVerbale);
        const datiId = clone.my_SezioneI__Rilevazione.my_DatiIdentificativi;

        datiId.my_TipoIncaricoEffettivo = tipoIncaricoEffettivo;
        datiId.my_TipoRevisione = tipoRevisione;
        datiId.my_AnnoRevisionePerAnnuali = annoRevisione;

        return clone;
    });
};

const calcolaTipoIncaricoEffettivo = (tipoIncarico: string, ckPeriodicRevi: string, anno2: string, dataCompilaz: string): string => {
    if (ckPeriodicRevi === '1') {
        if (tipoIncarico === '1' || tipoIncarico === '2') {
            return tipoIncarico;
        }

        if (tipoIncarico === '3' || tipoIncarico === '4') {
            const limiteA1 = `${anno2}-01-31`;

            return isNilOrEmpty(dataCompilaz) || dataCompilaz <= limiteA1 ? '1' : '3';
        }

        return tipoIncarico;
    }

    if (ckPeriodicRevi === '2') {
        if (tipoIncarico === '1') {
            return '3';
        }

        if (tipoIncarico === '2') {
            return '2';
        }

        if (tipoIncarico === '3' || tipoIncarico === '4') {
            return '3';
        }

        return tipoIncarico;
    }

    return tipoIncarico;
};

const calcolaTipoRevisioneAnnuale = (tipoIncaricoEffettivo: string, anno1: string, anno2: string): { tipoRevisione: string; annoRevisione: string } => {
    if (tipoIncaricoEffettivo === '1') {
        return {
            tipoRevisione: '- I',
            annoRevisione: anno1,
        };
    }

    if (tipoIncaricoEffettivo === '2') {
        return {
            tipoRevisione: '- II',
            annoRevisione: anno2,
        };
    }

    return {
        tipoRevisione: '',
        annoRevisione: '',
    };
};
